import { FunctionComponent, useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import { FormikErrors, FormikTouched } from 'formik';

import * as vp from 'vopay';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { PendingDataProvider } from '@/providers/PendingData.provider';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import { useClientData } from '@/providers/ClientData.provider';
import InteracInfoContext, { InteracDetails } from '@/components/Accounts/AccountCreation/Interac/InteracInfoContext';

import { SinglePayment, NewAccountData } from './types';
import ElinxSettingsDialogue from '@/components/Elinx/ElinxSettings/ElinxSettingsDialogue';
import { SinglePaymentDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import SinglePaymentForm from './SinglePaymentForm';
import PaylinkDialogue from '@/components/Accounts/AccountCreation/Dialogue/Paylink/PaylinkDialogue';
import {
  SendPaylinkDialogueHandler,
  DialogueHandlerProps as DialogueHandlerProps2,
} from '@/components/Accounts/AccountCreation/Dialogue/types';

import { SinglePaymentCtx } from '../Dialogue/SinglePaymentContext';
import SendPaylinkContext, {
  SendPaylinkCtx,
} from '@/components/Accounts/AccountCreation/Dialogue/Paylink/PaylinkContext';
import PaylinkCreationPage from '@/components/Accounts/AccountCreation/PaylinkCreationPage';

import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFetchFinancialInstitutionBranches from '@/hooks/useApi/useFetchFinancialInstitutionBranches';
import useFetchFinancialInstitutions from '@/hooks/useApi/useFetchFinancialInstitutions';
import useFetchInteracEmail from '@/hooks/useApi/useFetchInteracEmail';
import useFetchClientAccountBalance from '@/hooks/useApi/useFetchClientAccountBalance';
import { useAccountSummary } from '@/providers/AccountSummary.provider';
import usePostBulkPayoutRequest from '@/hooks/useApi/usePostBulkPayoutRequest';
import usePostEftWithdraw from '@/hooks/useApi/usePostEftWithdraw';
import usePostIQ11Tokenize from '@/hooks/useApi/usePostIQ11Tokenize';
import usePostScheduledEftWithdraw from '@/hooks/useApi/usePostScheduledEftWithdraw';
import usePostRtrWithdraw from '@/hooks/useApi/usePostRtrWithdraw';
import usePostSetMyClientBankAccount from '@/hooks/useApi/usePostSetMyClientBankAccount';
import usePostACHWithdraw from '@/hooks/useApi/usePostACHWithdraw';
import usePostACHWithdrawSchedule from '@/hooks/useApi/usePostACHWithdrawSchedule';
import useFetchElinxSettings from '@/hooks/useApi/useFetchElinxSettings';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';
import usePostRtpWithdraw from '@/hooks/useApi/usePostRtpWithdraw';
import usePostFedwireWithdraw from '@/hooks/useApi/usePostFedwireWithdraw';
import AvailableBalance from '@/components/UI/AvailableBalance/AvailableBalance';
import SinglePaymentBankInfoContext from './SinglePaymentBankInfoContext';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useVenmo from '@/hooks/useApi/useVenmo';
import { useDataFetchIndicator } from '@/hooks';
import usePayPal from '@/hooks/useApi/usePayPal';

type PaymentType =
  | 'Bank Account'
  | 'Credit Card'
  | 'Debit Card'
  | 'Interac e-Transfer'
  | 'Google Pay'
  | 'Apple Pay'
  | 'PayPal'
  | 'Venmo';

type TokenProps = {
  token: string;
  cardToken: string;
  type: PaymentType;
  currency: string;
  interacID: string;
  accountHolderName?: string;
  account?: {
    RecipientPhoneNumber?: string;
    RecipientName?: string;
    RecipientEmailAddress?: string;
    Question?: string;
    Answer?: string;
    ClientReferenceNumber?: string;
  };
};

const { Box, Title, toBool } = vp;

const SinglePaymentContainer: FunctionComponent = () => {
  const reviewRef = useRef<HTMLDivElement>(null);
  const { state } = useLocation<TokenProps>();
  const dialog = useAlertDialog();
  const history = useHistory();
  const { setOpenModule } = useOpenModule();
  const { getModuleTitle, setSubNavActiveModule, onSubNavClick } = useNavMenu();

  const cardTokenProp = state?.cardToken;
  const typeProp = state?.type;
  const currencyProp = state?.currency;
  const interacIDProp = state?.interacID;
  const tokenProp = state?.token || '';
  const accountHolderName = state?.accountHolderName;
  const { creditCards, fetchCreditCards } = useFetchClientCreditCards();
  const {
    response: { ClientAccountsEnabled, Country, AccountID, LoginUserName, DigitalWalletsEnabled, AccountName },
  } = useClientData();
  const [accountBalanceResponse, getAccountBalance] = usePostAccountBalance({});
  const [clientBankAccountsResponse, fetchClientBankAccounts, resetBankAccount] = useFetchClientBankAccounts({});
  const { userSettings } = useUserSettingsProvider();

  const [view, viewState] = useState<'form' | 'paylink'>('form');
  const [FinancialInstitutions, setFormattedFinancialInstitutions] = useState({});
  const [FormattedFinancialInstitutionBranches, setFormattedFinancialInstitutionBranches] = useState({});
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [showSinglePaymentErrorAlert, setShowSinglePaymentErrorAlert] = useState(false);
  const [showSinglePaymentConfirmAlert, setShowSinglePaymentConfirmAlert] = useState(false);
  const [singlePaymentError, setSinglePaymentError] = useState('');
  const [shouldWithdrawEft, setShouldWithdrawEft] = useState(false);
  const [isCreateAccount, setCreateAccount] = useState(false);
  const [shouldWithdrawScheduledEft, setShouldWithdrawScheduledEft] = useState(false);
  const [shouldTokenize, setShouldTokenize] = useState(false);
  const [shouldWithdrawInterac, setShouldWithdrawInterac] = useState(false);
  const [shouldWithdrawRtr, setShouldWithdrawRtr] = useState(false);
  const [shouldWithdrawRtp, setShouldWithdrawRtp] = useState(false);
  const [shouldWithdrawFedwire, setShouldWithdrawFedwire] = useState(false);
  const [canSubmitTransaction, setCanSubmitTransaction] = useState(false);

  const [formErrors, setFormErrors] = useState<FormikErrors<SinglePayment>>({});
  const [formTouched, setFormTouched] = useState<FormikTouched<SinglePayment>>({});
  const [submitTransaction, setSubmitTransaction] = useState(false);
  const [contactClientAccountID, setContactClientAccountID] = useState<string | undefined>(undefined);
  const [hasNoBankAccount, setHasNoBankAccount] = useState(false);
  const [hasVenmoAccount, setHasVenmoAccount] = useState(false);
  const [hasPayPalAccount, setHasPayPalAccount] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>(undefined);
  const [selectedBank, setSelectedBank] = useState<vp.ClientBankAccountsLists>();
  const [resetPendingData, shouldResetPendingData] = useState(false);
  const [shouldACHWithdraw, setShouldACHWithdraw] = useState(false);
  const [shouldACHFundSchedule, setShouldACHFundSchedule] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [shouldPostEmbedUrl, setShouldPostEmbedUrl] = useState(false);
  const [shouldSendPaylink, setShouldSendPaylink] = useState(false);
  const [showSendPaylinkSuccessAlert, setShowSendPaylinkSuccessAlert] = useState(false);
  const [showSendPaylinkErrorAlert, setShowSendPaylinkErrorAlert] = useState(false);
  const [singlePaymentValuesPresent, setSinglePaymentValuesPresent] = useState(false);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);
  const [sendPaylinkError, setSendPaylinkError] = useState('');
  const [payLink, setPayLink] = useState('');
  const [clientAccountDetail, getSelectedClientAccountDetail] = useState<vp.ClientAccount>();
  const { venmoWithdraw, fetchVenmoDefaultRecipient } = useVenmo();
  const { payPalWithdraw, fetchPayPalDefaultRecipient } = usePayPal();
  const [, setLoading] = useDataFetchIndicator();

  const [singlePaymentValues, setSinglePaymentValue] = useState<SinglePayment>({
    ABARoutingNumber: '',
    AccountConnectionMethod: '',
    AccountNumber: '',
    Address1: '',
    Address2: '',
    Amount: 0,
    Answer: '',
    BranchTransitNumber: '',
    City: '',
    ClientAccount: '',
    ClientReferenceNumber: '',
    CompanyName: '',
    Contact: '',
    ContactType: '',
    Country: '',
    Currency: '',
    Description: '',
    EndDate: '',
    Ending: '',
    EndingAfterPayments: '',
    EtransferConnectionType: '',
    FinancialInstitutionNumber: '',
    FirstName: '',
    Frequency: '',
    IdentificationNumber: '',
    InvoiceNumber: '',
    LastName: '',
    Message: '',
    MessageForRecipient: '',
    Nickname: '',
    Notes: '',
    PaymentMethod: '',
    PostalCode: '',
    ProvideOrRequestPaymentInformation: '',
    Province: '',
    Question: '',
    RecipientEmailAddress: '',
    RecipientName: '',
    RecipientPhoneNumber: '',
    ScheduleType: '',
    SemiMonthlyDates: '',
    SendEmail: false,
    SenderName: '',
    StartDate: '',
    TakeFromAccountOn: '',
    TransactionLabel: '',
    SendNotification: false,
    SendReminder: false,
    ReminderFrequency: '',
    NotificationMethod: 'email',
    SecCode: 'PPD',
  });
  const [bankAccountInfo, setBankAccountInfo] = useState<vp.ClientBankAccountsLists | undefined>({
    ABARoutingNumber: '',
    AccountHolderName: '',
    AccountNumber: '',
    Address: '',
    BankID: '',
    BranchTransitNumber: '',
    City: '',
    ClientAccountID: '',
    ClientControlledBankAccount: '',
    ClientReferenceNumber: '',
    Currency: '',
    DateAdded: '',
    EmailAddress: '',
    FinancialInstitutionNumber: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    InstitutionName: '',
    InveriteRequestGUID: '',
    IsDefault: '',
    Phone: '',
    PlaidAccountID: '',
    'PostalCode/ZipCode': '',
    'Province/State': '',
    Token: '',
    FiservToken: '',
    IntegrationType: '',
    MxAuthorizationCode: '',
    MxProcessorToken: '',
    PlaidProcessorToken: '',
  });
  const [selectedCurrency, setSelectedCurrency] = useState(
    currencyProp !== undefined ? currencyProp : Country === 'US' ? 'USD' : 'CAD',
  );
  const selectedPaymentMethod = typeProp !== undefined ? typeProp : 'Bank Account';
  const SinglePaymentBankInfoCtx = useContext(SinglePaymentBankInfoContext);
  const selectedAccount =
    typeProp === 'Bank Account' && tokenProp !== ''
      ? clientBankAccountsResponse?.BankAccounts[0]?.Token || ''
      : !!cardTokenProp
        ? String(creditCards.find((key) => creditCards[Number(key)].CreditCardToken === cardTokenProp))
        : interacIDProp !== undefined
          ? interacIDProp
          : '';

  useEffect(() => {
    fetchCreditCards({
      PortalUser: LoginUserName,
      ClientControlled: ClientAccountsEnabled === '1',
    });
  }, []);

  useEffect(() => {
    if (clientBankAccountsResponse.Success && tokenProp !== '') {
      setBankAccountInfo(clientBankAccountsResponse.BankAccounts[0]);
      setSelectedBank(clientBankAccountsResponse.BankAccounts[0]);
      SinglePaymentBankInfoCtx.updateBankAccount({
        ...clientBankAccountsResponse.BankAccounts[0],
      });
    }
  }, [clientBankAccountsResponse]);

  const [withdrawRequest, setWithdrawRequest] = useState<SinglePayment>({
    Currency: selectedCurrency,
    PaymentMethod: selectedPaymentMethod,
    BankingPaymentRail: Country === 'CA' ? 'eft' : 'ach',
    Contact: selectedAccount,
    Amount: 0,
    ScheduleType: 'single',
    TakeFromAccountOn: String(Date.now()),
    Frequency: 'monthly',
    SemiMonthlyDates: '',
    StartDate: String(Date.now()),
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    Message: '',
    Notes: '',
    ClientAccount: '',
    TransactionLabel: '',
    MessageForRecipient: '',
    EndingAfterPayments: '',
    IdentificationNumber: '',
    ClientReferenceNumber: '',
    FirstName: '',
    LastName: '',
    Address1: '',
    Address2: '',
    City: '',
    Province: '',
    Country,
    PostalCode: '',
    FinancialInstitutionNumber: '',
    BranchTransitNumber: '',
    ABARoutingNumber: '',
    AccountNumber: '',
    ContactType: 'Individual',
    CompanyName: '',
    EtransferConnectionType: 'email',
    RecipientName: '',
    RecipientPhoneNumber: '',
    RecipientEmailAddress: '',
    Question: '',
    Answer: '',
    Nickname: '',
    ProvideOrRequestPaymentInformation: 'provide',
    AccountConnectionMethod: 'Manual',
    InvoiceNumber: '',
    SendEmail: false,
    SenderName: AccountName,
    SendNotification: false,
    SendReminder: false,
    ReminderFrequency: '7',
    NotificationMethod: 'email',
    CancellationPeriod: 14, //default is 14 days
    ReceiverPhoneNumber: '',
  });

  const { accountBalance, setAccountBalance } = useAccountSummary();
  const [clientAccountBalanceResponse, getClientAccountBalance] = useFetchClientAccountBalance({});
  const availableBalance =
    Number(ClientAccountsEnabled) === 1 ? clientAccountBalanceResponse.AvailableFunds : accountBalance?.AvailableFunds;

  useEffect(() => {
    if (accountBalanceResponse.Success) {
      setAccountBalance(accountBalanceResponse);
    }
  }, [accountBalanceResponse]);

  useEffect(() => {
    if (contactClientAccountID !== undefined) {
      getClientAccountBalance({
        ClientAccountID: contactClientAccountID || '',
        Currency: withdrawRequest.Currency || 'CAD',
      });
    }
  }, [contactClientAccountID, withdrawRequest.Currency]);

  const [interacInfo, setInteracInfo] = useState({
    isInteracQuestionsRequired: true,
    addInteracEmail: false,
    interacDetails: {
      RecipientPhoneNumber: '',
      RecipientName: '',
      RecipientEmailAddress: '',
      Question: '',
      Answer: '',
      ClientReferenceNumber: '',
    },
  });

  const updateBankAccount = (bankDetails?: vp.ClientBankAccountsLists) => {
    setBankAccountInfo(bankDetails);
  };

  const updateInterac = (
    addInteracEmail: boolean,
    isInteracQuestionsRequired?: boolean,
    interacDetails?: InteracDetails,
  ) => {
    setInteracInfo({
      addInteracEmail,
      isInteracQuestionsRequired: isInteracQuestionsRequired || false,

      interacDetails: {
        ...interacInfo.interacDetails,
        ...interacDetails,
        ClientReferenceNumber: interacDetails?.ClientReferenceNumber || '',
      },
    });
  };
  const [interacInfoWithUpdate, setInteracInfoWithUpdate] = useState({
    isInteracQuestionsRequired: true,
    addInteracEmail: false,
    interacDetails: {
      RecipientPhoneNumber: '',
      RecipientName: '',
      RecipientEmailAddress: '',
      Question: '',
      Answer: '',
      ClientReferenceNumber: '',
    },
    updateInterac,
  });

  const [common, setCommon] = useState({
    Address1: '',
    Amount: 0,
    AccountNumber: '',
    ABARoutingNumber: '',
    BranchTransitNumber: '',
    City: '',
    CompanyName: '',
    Country: '',
    ClientReferenceNumber: '',
    FinancialInstitutionNumber: '',
    FirstName: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    LastName: '',
    PortalUser: '',
    PhoneNumber: '',
    Province: '',
    PostalCode: '',
    Token: '',
    TransactionLabel: '',
  });

  const SendPaylink: SendPaylinkCtx = {
    showSendPaylinkSuccessAlert,
    showSendPaylinkErrorAlert,
    sendPaylinkError,
    payLink,
  };

  const [financialInstitutionsResp, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [financialInstitutionBranchesResponse, fetchFinancialInstitutionBranches] =
    useFetchFinancialInstitutionBranches({});
  const [interacEmailsResponse, fetchInteracEmails] = useFetchInteracEmail({});
  const [setDefautlBankAccountResponse, setDefautlBankAccount] = usePostSetMyClientBankAccount({});
  const [embedUrlResponse, postEmbedUrlResponse] = usePostEmbedUrl({});
  const [paylinkResponse, postSendPaylink] = usePostSendPaylink({});

  const [scheduledEftWithdrawResponse, postScheduledEftWithdraw] = usePostScheduledEftWithdraw({});
  const [eftWithdrawResponse, postEftWithdraw] = usePostEftWithdraw({});
  const [bulkPayoutResponse, postBulkPayoutResponse] = usePostBulkPayoutRequest({});
  const [tokenizeResponse, postTokenize] = usePostIQ11Tokenize({});
  const [rtrWithdrawResponse, postRtrWithdraw] = usePostRtrWithdraw({});
  const [ACHWithdrawResponse, postACHWithdraw] = usePostACHWithdraw({});
  const [ACHWithdrawScheduleResponse, postACHWithdrawSchedule] = usePostACHWithdrawSchedule({});
  const [, , , fetchElixSettings] = useFetchElinxSettings({});
  const [rtpWithdrawResponse, postRtpWithdraw] = usePostRtpWithdraw({});
  const [fedwireWithdrawResponse, postFedwireWithdraw] = usePostFedwireWithdraw({});

  useEffect(() => {
    setInteracInfoWithUpdate({
      ...interacInfo,
      updateInterac,
    });
  }, [interacInfo]);

  useEffect(() => {
    if (state?.interacID !== undefined && state?.account !== undefined) {
      setInteracInfo({
        isInteracQuestionsRequired: true,
        addInteracEmail: false,
        interacDetails: {
          RecipientPhoneNumber: state?.account.RecipientPhoneNumber || '',
          RecipientName: state?.account.RecipientName || '',
          RecipientEmailAddress: state?.account.RecipientEmailAddress || '',
          Question: state?.account.Question || '',
          Answer: state?.account.Answer || '',
          ClientReferenceNumber: state?.account.ClientReferenceNumber || '',
        },
      });
    }
  }, [state?.interacID]);
  const [clientAccountInteracInitialDetail, fetchClientAccountInteracInitialDetail] = useState({
    RecipientName: '',
    RecipientEmailAddress: '',
  });
  const [clientAccountInteracDetail, fetchClientAccountInteracDetail] = useState({
    Question: '',
    Answer: '',
    RecipientPhoneNumber: '',
  });
  const [shouldAddEmail, setShouldAddEmail] = useState(false);
  useEffect(() => {
    if (clientAccountDetail) {
      fetchClientBankAccounts({
        ClientAccountID: contactClientAccountID,
        Timezone: userSettings.timezone,
      });

      if (Country === 'CA') {
        fetchInteracEmails({
          ClientAccountID: contactClientAccountID,
          RecipientName: '',
          RecipientEmailAddress: '',
          ClientReferenceNumber: '',
          ClientControlled: false,
        });
      }
      if (DigitalWalletsEnabled) {
        (async () => {
          if (Country === 'US') {
            try {
              setLoading(true);
              const venmoRecipients = await fetchVenmoDefaultRecipient({
                ClientAccountID: contactClientAccountID,
              });
              setHasVenmoAccount(!!venmoRecipients.DefaultVenmoRecipient);
            } catch {
              setHasVenmoAccount(false);
            } finally {
              setLoading(false);
            }
          } else {
            try {
              setLoading(true);
              const paypalRecipients = await fetchPayPalDefaultRecipient({
                ClientAccountID: contactClientAccountID,
              });

              setHasPayPalAccount(!!paypalRecipients.DefaultPayPalRecipient);
            } catch {
              setHasPayPalAccount(false);
            } finally {
              setLoading(false);
            }
          }
        })();
      }

      fetchClientAccountInteracInitialDetail({
        RecipientName: `${clientAccountDetail?.FirstName || ''} ${clientAccountDetail?.LastName || ''}`,
        RecipientEmailAddress: clientAccountDetail?.EmailAddress,
      });
    }
  }, [contactClientAccountID, paymentMethod, withdrawRequest.Currency]);

  useEffect(() => {
    // client accout bank
    if (vp.toBool(ClientAccountsEnabled) && clientBankAccountsResponse.Success) {
      if (
        withdrawRequest.PaymentMethod === 'Bank Account' &&
        (Object.keys(clientBankAccountsResponse.BankAccounts).length === 0 ||
          !Object.values(clientBankAccountsResponse.BankAccounts || {}).filter(
            (account) =>
              account.ClientAccountID === clientAccountDetail?.ClientAccountID &&
              account.IsDefault === '1' &&
              account.Currency === withdrawRequest.Currency,
          )[0])
      ) {
        setHasNoBankAccount(true);
        setCreateAccount(true);
        setCommon((prevCommon) => ({
          ...prevCommon,
          CompanyName: clientAccountDetail?.ClientName || '',
        }));
        setCanSubmit(false);
      } else {
        setHasNoBankAccount(false);
        setCreateAccount(false);
        setCanSubmit(true);
      }
    }
  }, [clientBankAccountsResponse]);

  useEffect(() => {
    if (interacEmailsResponse.Success) {
      const last = Object.keys(interacEmailsResponse?.InteracEmails)[
        Object.keys(interacEmailsResponse?.InteracEmails).length - 1
      ];

      fetchClientAccountInteracDetail({
        Question: interacEmailsResponse?.InteracEmails[last]?.Question || '',
        Answer: interacEmailsResponse?.InteracEmails[last]?.Answer || '',
        RecipientPhoneNumber: interacEmailsResponse?.InteracEmails[last]?.RecipientPhoneNumber || '',
      });
    }
  }, [interacEmailsResponse]);

  useEffect(() => {
    setShouldAddEmail(clientAccountInteracDetail.Question === '' || clientAccountInteracDetail.Answer === '');
  }, [clientAccountInteracDetail]);

  useEffect(() => {
    if (vp.toBool(ClientAccountsEnabled)) {
      setInteracInfo({
        isInteracQuestionsRequired: true,
        addInteracEmail: shouldAddEmail,
        interacDetails: {
          ...clientAccountInteracInitialDetail,
          ...clientAccountInteracDetail,
          ClientReferenceNumber: '',
        },
      });
    }
  }, [clientAccountInteracInitialDetail, clientAccountInteracDetail, shouldAddEmail]);

  const getElinxSettings = async () => {
    try {
      const res = await fetchElixSettings({});
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      } else {
        setIsVisible(false);
        setWithdrawRequest({
          ...withdrawRequest,
          SendNotification: toBool(res.ELinxSettings.ReceiverNotificationsEnabled),
          SendReminder: toBool(res.ELinxSettings.ELinxReminderEnabled),
          ReminderFrequency: res.ELinxSettings.ELinxReminderFrequency,
          NotificationMethod: res.ELinxSettings.DefaultNotificationMethod,
        });
      }
    } catch (err) {
      console.error(err);
      dialog.caution('Error', 'Failed to Fetch eLinx Settings', {
        abortLabel: 'Dismiss',
      });
    }
  };

  useEffect(() => {
    fetchFinancialInstitutions({
      Country,
    });
    getElinxSettings();
  }, []);

  type FinancialInstitutionsType = {
    [key: string]: string;
  };

  useEffect(() => {
    if (financialInstitutionsResp.Success) {
      const formattedFinancialInstitutions: FinancialInstitutionsType = Object.keys(
        financialInstitutionsResp.FinancialInstitutions,
      ).reduce((formatted: FinancialInstitutionsType, key) => {
        const { FinancialInstitutionNumber, FIName } = financialInstitutionsResp.FinancialInstitutions[key];
        formatted[FinancialInstitutionNumber] = `${FinancialInstitutionNumber} <br/> ${FIName}`;

        return formatted;
      }, {});
      setFormattedFinancialInstitutions(formattedFinancialInstitutions);
    }
  }, [financialInstitutionsResp]);

  useEffect(() => {
    if (financialInstitutionBranchesResponse.Success) {
      const formattedFinancialInstitutionBranches: FinancialInstitutionsType =
        financialInstitutionBranchesResponse.Branches.reduce((formatted: FinancialInstitutionsType, branch) => {
          const { BranchTransitNumber, Address, TownCity, PostalCode, Province } = branch;
          formatted[BranchTransitNumber] =
            `${BranchTransitNumber} <br/> ${Address}, ${TownCity}, ${PostalCode}, ${Province}`;

          return formatted;
        }, {});

      setFormattedFinancialInstitutionBranches(formattedFinancialInstitutionBranches);
    }
  }, [financialInstitutionBranchesResponse]);

  useEffect(() => {
    if (canSubmit === true) {
      if (isCreateAccount) {
        if (getAccountCreationValue(withdrawRequest.PaymentMethod) === 'bank') {
          const NewBankAccount: vp.ClientBankAccountsLists = {
            ABARoutingNumber: String(withdrawRequest.ABARoutingNumber),
            AccountHolderName:
              withdrawRequest.CompanyName || `${withdrawRequest.FirstName || ''} ${withdrawRequest.LastName || ''}`,
            AccountNumber: String(withdrawRequest.AccountNumber),
            Address: `${withdrawRequest.Address1 || ''} ${withdrawRequest.Address2 || ''}`,
            BranchTransitNumber: String(withdrawRequest.BranchTransitNumber),
            City: withdrawRequest.City || '',
            ClientReferenceNumber: withdrawRequest.ClientReferenceNumber || '',
            Currency: withdrawRequest.Currency || '',
            EmailAddress: '',
            FinancialInstitutionNumber: String(withdrawRequest.FinancialInstitutionNumber),
            'PostalCode/ZipCode': withdrawRequest.PostalCode || '',
            'Province/State': withdrawRequest.Province || '',
            Token: '',
            Phone: '',
            BankID: '',
            ClientAccountID: '',
            ClientControlledBankAccount: '',
            DateAdded: '',
            FlinksAccountID: '',
            FlinksLoginID: '',
            InstitutionName: '',
            InveriteRequestGUID: '',
            IsDefault: '',
            PlaidAccountID: '',
            FiservToken: '',
            IntegrationType: '',
            MxAuthorizationCode: '',
            MxProcessorToken: '',
            PlaidProcessorToken: '',
          };
          setSelectedBank(NewBankAccount);
        } else if (getAccountCreationValue(withdrawRequest.PaymentMethod) === 'e-transfer') {
          setShouldAddEmail(true);
        }
      }
    }
  }, [canSubmit, withdrawRequest]);

  const SinglePaymentContext: SinglePaymentCtx = {
    showSinglePaymentErrorAlert,
    singlePaymentError,
    showSinglePaymentConfirmAlert,
  };

  useEffect(() => {
    if (singlePaymentValuesPresent && submissionConfirmed) {
      setSinglePaymentValuesPresent(false);
      setSubmissionConfirmed(false);
      shouldResetPendingData(true);

      if (
        singlePaymentValues?.Contact === 'request' &&
        (getAccountCreationValue(singlePaymentValues?.PaymentMethod) === 'bank' ||
          singlePaymentValues?.PaymentMethod === 'Credit Card' ||
          singlePaymentValues?.PaymentMethod === 'PayPal' ||
          singlePaymentValues?.PaymentMethod === 'Venmo')
      ) {
        setShouldPostEmbedUrl(true);
        postEmbedUrlResponse(buildEmbedUrlParams(singlePaymentValues));
      } else if (!isCreateAccount) {
        setCanSubmitTransaction(true);
      } else {
        setSubmitData(singlePaymentValues);
        if (vp.toBool(ClientAccountsEnabled) || paymentMethod === 'VoPay Instant') {
          setCanSubmit(false);
          setShouldTokenize(true);
          postTokenize({
            PortalUser: common.PortalUser,
            AccountHolderName:
              common.CompanyName && common.CompanyName !== ''
                ? common.CompanyName
                : `${common.FirstName || ''} ${common.LastName || ''}`,
            AccountNumber: common.AccountNumber || singlePaymentValues?.AccountNumber,
            FinancialInstitutionNumber:
              common.FinancialInstitutionNumber || singlePaymentValues?.FinancialInstitutionNumber,
            BranchTransitNumber: common.BranchTransitNumber || singlePaymentValues?.BranchTransitNumber,
            ABARoutingNumber: common.ABARoutingNumber || singlePaymentValues?.ABARoutingNumber,
            ClientReferenceNumber: common.ClientReferenceNumber,
            ClientControlled: false,
            ClientAccountID: contactClientAccountID || '',
            Currency: singlePaymentValues?.Currency || 'CAD',
          });
        } else {
          setCanSubmitTransaction(true);
        }
      }
    }
  }, [singlePaymentValuesPresent, submissionConfirmed]);

  useEffect(() => {
    const bankAccountDetail = selectedBank;
    const clientAccountsEnabled = vp.toBool(ClientAccountsEnabled);
    const nameParts = bankAccountDetail?.AccountHolderName?.trim().split(' ') || '';
    const isCompanyName = nameParts.length > 2 || nameParts.length < 2;

    setCommon({
      PortalUser: AccountID || '',
      Amount: withdrawRequest.Amount || 0,
      FinancialInstitutionNumber: clientAccountsEnabled ? '' : bankAccountDetail?.FinancialInstitutionNumber || '',
      BranchTransitNumber: clientAccountsEnabled ? '' : bankAccountDetail?.BranchTransitNumber || '',
      ABARoutingNumber: clientAccountsEnabled ? '' : bankAccountDetail?.ABARoutingNumber || '',
      AccountNumber: clientAccountsEnabled ? '' : bankAccountDetail?.AccountNumber || '',
      FirstName: !isCompanyName ? bankAccountDetail?.AccountHolderName.split(' ')[0] || '' : '',
      LastName: !isCompanyName ? bankAccountDetail?.AccountHolderName.split(' ')[1] || '' : ' ',
      CompanyName: isCompanyName ? bankAccountDetail?.AccountHolderName || '' : '',
      PhoneNumber: vp.NumbersOnly(bankAccountDetail?.Phone || ''),
      Address1: bankAccountDetail?.Address || '',
      City: bankAccountDetail?.City || '',
      Province: bankAccountDetail?.['Province/State'] || '',
      Country: Country || '',
      Token: clientAccountsEnabled ? '' : bankAccountDetail?.Token || '',
      PostalCode: bankAccountDetail?.['PostalCode/ZipCode'] || '',
      ClientReferenceNumber: bankAccountDetail?.ClientReferenceNumber || '',
      FlinksAccountID: clientAccountsEnabled ? '' : bankAccountDetail?.FlinksAccountID || '',
      FlinksLoginID: clientAccountsEnabled ? '' : bankAccountDetail?.FlinksLoginID || '',
      TransactionLabel: withdrawRequest.TransactionLabel || '',
    });
  }, [selectedBank]);

  const setAccountDefault = (tokenAccount: string) => {
    // set my bank account
    const ClientAccountID = contactClientAccountID;
    setDefautlBankAccount({
      ClientAccountID,
      SetAsDefault: true,
      Token: tokenAccount,
      Purpose: 'operational',
      PortalUser: LoginUserName,
    });
  };

  useEffect(() => {
    if (shouldTokenize) {
      if (tokenizeResponse.Success) {
        setCommon((prevCommon) => ({
          ...prevCommon,
          Token: tokenizeResponse.Token,
        }));
        if (vp.toBool(ClientAccountsEnabled)) {
          setAccountDefault(tokenizeResponse.Token);
          setCanSubmitTransaction(true);
          setCanSubmit(true);
        } else {
          setCanSubmitTransaction(true);
          setCanSubmit(true);
        }
      } else {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(tokenizeResponse.ErrorMessage);
      }
      setShouldTokenize(false);
    }
  }, [tokenizeResponse]);

  useEffect(() => {
    if (setDefautlBankAccountResponse.Success) {
      setCanSubmit(true);
    }
  }, [setDefautlBankAccountResponse]);

  useEffect(() => {
    if (shouldWithdrawEft) {
      const { Success, ErrorMessage } = eftWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/EFTWithdrawal/${eftWithdrawResponse.TransactionID}/${
            withdrawRequest?.Currency || ''
          }/`,
          state: {
            transactionId: eftWithdrawResponse.TransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldWithdrawEft(false);
    }

    if (shouldWithdrawScheduledEft) {
      const { Success, ErrorMessage } = scheduledEftWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${scheduledEftWithdrawResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: scheduledEftWithdrawResponse.ScheduledTransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('transactions');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldWithdrawScheduledEft(false);
    }

    if (shouldWithdrawRtr) {
      const { Success, ErrorMessage } = rtrWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/VoPayInstantWithdraw/${rtrWithdrawResponse.TransactionID}/${
            withdrawRequest?.Currency || ''
          }/`,
          state: {
            transactionId: rtrWithdrawResponse.TransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('transactions');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldWithdrawRtr(false);
    }

    if (shouldWithdrawInterac) {
      const { Success, ErrorMessage } = bulkPayoutResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/InteracMoneyRequest/${bulkPayoutResponse.TransactionID}/${
            withdrawRequest.Currency || ''
          }/`,
          state: {
            transactionId: bulkPayoutResponse.TransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldWithdrawInterac(false);
    }

    if (ACHWithdrawResponse && shouldACHWithdraw) {
      const { Success, ErrorMessage } = ACHWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/ACHWithdrawal/${ACHWithdrawResponse.TransactionID}/${
            withdrawRequest?.Currency || ''
          }/`,
          state: {
            transactionId: ACHWithdrawResponse.TransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldACHWithdraw(false);
    }

    if (shouldACHFundSchedule === true && ACHWithdrawScheduleResponse) {
      const { Success, ErrorMessage } = ACHWithdrawScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${ACHWithdrawScheduleResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: ACHWithdrawScheduleResponse.ScheduledTransactionID,
            activeModule: 'singleCollection',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }
        setCanSubmit(false);
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldACHFundSchedule(false);
    }

    if (shouldPostEmbedUrl) {
      const { Success, ErrorMessage } = embedUrlResponse;

      if (Success === false && ErrorMessage !== '') {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
        setShouldPostEmbedUrl(false);
      }
      shouldResetPendingData(false);
    }

    if (rtpWithdrawResponse && shouldWithdrawRtp) {
      const { Success, ErrorMessage } = rtpWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/RTPWithdrawal/${rtpWithdrawResponse.TransactionID}/${
            withdrawRequest?.Currency || ''
          }/`,
          state: {
            transactionId: rtpWithdrawResponse.TransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldACHWithdraw(false);
    }

    if (fedwireWithdrawResponse && shouldWithdrawFedwire) {
      const { Success, ErrorMessage } = fedwireWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/RTPWithdrawal/${fedwireWithdrawResponse.TransactionID}/${
            withdrawRequest?.Currency || ''
          }/`,
          state: {
            transactionId: fedwireWithdrawResponse.TransactionID,
            activeModule: 'singlePayment',
            hasNewTransactionButton: true,
          },
        });

        if (isCreateAccount) {
          fetchCreditCards({
            PortalUser: LoginUserName,
            ClientControlled: ClientAccountsEnabled === '1',
          });
          setCreateAccount(false);
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        setCanSubmit(false);
      } else if (Success === false) {
        setShowSinglePaymentErrorAlert(true);
        setSinglePaymentError(ErrorMessage);
      }
      setShouldACHWithdraw(false);
    }
  }, [
    eftWithdrawResponse,
    scheduledEftWithdrawResponse,
    bulkPayoutResponse,
    rtrWithdrawResponse,
    ACHWithdrawResponse,
    ACHWithdrawScheduleResponse,
    embedUrlResponse,
    rtpWithdrawResponse,
    fedwireWithdrawResponse,
  ]);

  useEffect(() => {
    if (embedUrlResponse.Success && shouldPostEmbedUrl) {
      setShouldSendPaylink(true);
      const now = moment().format('YYYY-MM-DD');
      const isToday =
        moment(now).format('YYYY-MM-DD') === moment(Number(withdrawRequest?.StartDate)).format('YYYY-MM-DD') ||
        withdrawRequest?.StartDate === undefined ||
        withdrawRequest?.StartDate === '';
      postSendPaylink({
        SenderName: withdrawRequest?.SenderName || '',
        ReceiverName: withdrawRequest?.CompanyName
          ? `${withdrawRequest?.CompanyName || ''}`
          : withdrawRequest?.RecipientName
            ? `${withdrawRequest?.RecipientName || ''}`
            : `${withdrawRequest?.FirstName || ''} ${withdrawRequest?.LastName || ''}`,
        ReceiverEmailAddress: withdrawRequest?.RecipientEmailAddress || '',
        ReceiverPhoneNumber: withdrawRequest?.ReceiverPhoneNumber || '',
        IframeKey: embedUrlResponse.IframeKey || '',
        InvoiceNumber: withdrawRequest?.InvoiceNumber || '',
        ClientReferenceNumber: withdrawRequest?.ClientReferenceNumber || '',
        Note: withdrawRequest?.Notes || '',
        RecipientMessage: withdrawRequest?.MessageForRecipient || '',
        PaymentType: 'withdraw',
        Amount: withdrawRequest?.Amount,
        Currency: withdrawRequest.Currency,
        Frequency: !isToday ? withdrawRequest?.ScheduleType : '',
        NameOfFrequency: !isToday ? withdrawRequest?.Frequency : '',
        ScheduleStartDate: !isToday ? moment(Number(withdrawRequest?.StartDate)).format('YYYY-MM-DD') : '',
        ScheduleEndDate:
          !isToday && withdrawRequest?.Ending === 'End Date'
            ? moment(Number(withdrawRequest?.EndDate)).format('YYYY-MM-DD')
            : '',
        EndingAfterPayments:
          !isToday && withdrawRequest?.Ending === 'Payments' ? withdrawRequest?.EndingAfterPayments : '',
        SemiMonthlyFrequencyType: !isToday ? withdrawRequest?.SemiMonthlyDates : '',
        ClientAccountID: contactClientAccountID,
        SendEmail: withdrawRequest?.SendEmail || false,
        BankingPaymentRail:
          withdrawRequest.PaymentMethod === 'Bank Account' ? withdrawRequest?.BankingPaymentRail || 'eft' : undefined,
        SendNotification: withdrawRequest.SendNotification,
        SendReminder: withdrawRequest.SendReminder,
        ReminderFrequency: withdrawRequest.ReminderFrequency,
        NotificationMethod: withdrawRequest.NotificationMethod,
      });
      setShouldPostEmbedUrl(false);
    }
  }, [embedUrlResponse.Success, shouldPostEmbedUrl]);

  useEffect(() => {
    if (canSubmitTransaction && canSubmit && Object.values(formErrors).length === 0) {
      if (
        withdrawRequest?.PaymentMethod === 'Bank Account' &&
        ((withdrawRequest?.ScheduleType === 'single' &&
          withdrawRequest?.TakeFromAccountOn &&
          vp.IsTodayDate(moment(Number(withdrawRequest.TakeFromAccountOn)).format('YYYY-MM-DD'))) ||
          (Country === 'CA' && withdrawRequest.Currency === 'USD'))
      ) {
        if (withdrawRequest.BankingPaymentRail === 'eft') {
          setShouldWithdrawEft(true);
          postEftWithdraw({
            ...common,
            Amount: withdrawRequest?.Amount || 0,
            Currency: withdrawRequest.Currency || '',
            Notes: withdrawRequest.Notes || '',
            ClientReferenceNumber: common?.ClientReferenceNumber || withdrawRequest?.ClientReferenceNumber || '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
          });
        }
        if (withdrawRequest.BankingPaymentRail === 'vopayinstant') {
          setShouldWithdrawRtr(true);
          postRtrWithdraw({
            ...common,
            Amount: withdrawRequest?.Amount || 0,
            Notes: withdrawRequest.Notes || '',
            ClientReferenceNumber: common?.ClientReferenceNumber || withdrawRequest?.ClientReferenceNumber || '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
          });
        }
        if (withdrawRequest.BankingPaymentRail === 'ach') {
          setShouldACHWithdraw(true);
          postACHWithdraw({
            ...common,
            Amount: withdrawRequest?.Amount || 0,
            Currency: withdrawRequest?.Currency || 'USD',
            PhoneNumber: withdrawRequest?.RecipientPhoneNumber || common?.PhoneNumber || '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
            State: withdrawRequest?.Province || common?.Province || '',
            ZipCode: withdrawRequest?.PostalCode || common?.PostalCode || '',
            IdentificationNumber: withdrawRequest?.IdentificationNumber || '',
            EmailAddress: withdrawRequest?.RecipientEmailAddress || selectedBank?.EmailAddress || '',
            SecCode: withdrawRequest?.SecCode || 'PPD',
          });
        }
        if (withdrawRequest.BankingPaymentRail === 'rtp') {
          setShouldWithdrawRtp(true);
          postRtpWithdraw({
            ...common,
            Amount: withdrawRequest?.Amount || 0,

            RecipientName: withdrawRequest.RecipientName,
            ClientReferenceNumber: withdrawRequest.ClientAccount,
          });
        }
        if (withdrawRequest.BankingPaymentRail === 'fedwire') {
          setShouldWithdrawFedwire(true);
          postFedwireWithdraw({
            Amount: withdrawRequest?.Amount || 0,
            Address1: withdrawRequest?.Address1,
            City: withdrawRequest.City,
            State: withdrawRequest?.Province,
            Country: 'US',
            ZipCode: withdrawRequest?.PostalCode,
            AccountNumber: withdrawRequest?.AccountNumber,
            ABARoutingNumber: withdrawRequest?.ABARoutingNumber,
            FirstName: withdrawRequest?.FirstName,
            LastName: withdrawRequest?.LastName,
            CompanyName: withdrawRequest?.CompanyName,
          });
        }
      } else if (
        (withdrawRequest?.PaymentMethod === 'Bank Account' && withdrawRequest?.ScheduleType === 'recurring') ||
        (withdrawRequest?.TakeFromAccountOn &&
          !vp.IsTodayDate(moment(Number(withdrawRequest.TakeFromAccountOn)).format('YYYY-MM-DD')))
      ) {
        if (Country === 'US') {
          setShouldACHFundSchedule(true);
          postACHWithdrawSchedule({
            ...common,
            Frequency: withdrawRequest?.ScheduleType || '',
            NameOfFrequency: withdrawRequest?.Frequency || '',
            SemiMonthlyFrequencyType: withdrawRequest?.SemiMonthlyDates || '',
            ScheduleStartDate:
              withdrawRequest?.ScheduleType === 'recurring'
                ? moment(Number(withdrawRequest.StartDate)).format('YYYY-MM-DD') || ''
                : moment(Number(withdrawRequest.TakeFromAccountOn)).format('YYYY-MM-DD') || '',
            ScheduleEndDate:
              withdrawRequest.Ending === 'End Date' && withdrawRequest.ScheduleType === 'recurring'
                ? moment(Number(withdrawRequest.EndDate)).format('YYYY-MM-DD') || ''
                : '',
            PhoneNumber: withdrawRequest?.RecipientPhoneNumber || common?.PhoneNumber || '',
            EndingAfterPayments: withdrawRequest.Ending === 'Payments' ? withdrawRequest.EndingAfterPayments || '' : '',
            Description: withdrawRequest?.Description || '',
            Address1: withdrawRequest?.Address1 || common?.Address1 || '',
            City: withdrawRequest?.City || common?.City || '',
            State: withdrawRequest?.Province || common.Province || '',
            ZipCode: withdrawRequest?.PostalCode || common?.PostalCode || '',
            Currency: withdrawRequest?.Currency || 'USD',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
            IdentificationNumber: withdrawRequest?.IdentificationNumber || '',
          });
        } else {
          setShouldWithdrawScheduledEft(true);
          postScheduledEftWithdraw({
            ...common,
            Amount: withdrawRequest?.Amount || 0,
            Frequency: withdrawRequest?.ScheduleType || '',
            NameOfFrequency: withdrawRequest?.Frequency || '',
            ScheduleStartDate:
              withdrawRequest?.ScheduleType === 'recurring'
                ? moment(Number(withdrawRequest.StartDate)).format('YYYY-MM-DD') || ''
                : moment(Number(withdrawRequest.TakeFromAccountOn)).format('YYYY-MM-DD') || '',
            ScheduleEndDate:
              withdrawRequest.Ending === 'End Date' && withdrawRequest.ScheduleType === 'recurring'
                ? moment(Number(withdrawRequest?.EndDate || '')).format('YYYY-MM-DD')
                : '',
            EndingAfterPayments: withdrawRequest.Ending === 'Payments' ? withdrawRequest.EndingAfterPayments || '' : '',
            Description: withdrawRequest?.Description || '',
            SemiMonthlyFrequencyType: withdrawRequest?.SemiMonthlyDates || '',
            EmailAddress: '',
            DOB: '',
            IPAddress: '',
            PlaidAccountID: selectedBank?.PlaidAccountID || '',
            PlaidPublicToken: '',
            PlaidAccessToken: '',
            PlaidProcessorToken: '',
            InveriteRequestGUID: selectedBank?.InveriteRequestGUID || '',
            IdempotencyKey: '',
            ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
            Notes: withdrawRequest.Notes,
          });
        }
      } else if (withdrawRequest?.PaymentMethod === 'Interac e-Transfer') {
        setShouldWithdrawInterac(true);
        postBulkPayoutResponse({
          PortalUser: AccountID || '',
          Amount: withdrawRequest?.Amount || 0,
          Currency: withdrawRequest?.Currency || '',
          EmailAddress:
            !vp.toBool(ClientAccountsEnabled) && withdrawRequest?.EtransferConnectionType === 'email'
              ? withdrawRequest?.RecipientEmailAddress || ''
              : '',
          PhoneNumber:
            withdrawRequest?.EtransferConnectionType === 'phone' ? withdrawRequest?.RecipientPhoneNumber : '',
          RecipientName: withdrawRequest?.RecipientName || '',
          Memo: withdrawRequest.Message || withdrawRequest?.MessageForRecipient || '',
          ClientReferenceNumber: withdrawRequest?.ClientReferenceNumber || '',
          Notes: withdrawRequest?.Notes || '',
          ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
          Language: 'en',
          Question: withdrawRequest?.Question || '',
          Answer: withdrawRequest?.Answer || '',
          MessageForRecipient: withdrawRequest.Message || '',
          CancellationPeriod: withdrawRequest.CancellationPeriod,
        });
      } else if (withdrawRequest?.PaymentMethod === 'PayPal') {
        (async () => {
          try {
            setLoading(true);
            const paypalResponse = await payPalWithdraw({
              PayPalToken: withdrawRequest.Contact,
              Amount: withdrawRequest?.Amount || 0,
              ClientReferenceNumber: withdrawRequest?.ClientReferenceNumber,
              ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest.ClientAccount : '',
              Currency: withdrawRequest?.Currency || '',
              Notes: withdrawRequest?.Notes || '',
            });
            const { Success, ErrorMessage } = paypalResponse;

            if (Success) {
              history.push({
                pathname: `/transactions-history/details/PayPalWithdrawal/${paypalResponse.TransactionID}/${
                  withdrawRequest.Currency || ''
                }/`,
                state: {
                  transactionId: paypalResponse.TransactionID,
                  activeModule: 'singlePayment',
                  hasNewTransactionButton: true,
                },
              });

              setCanSubmit(false);
            } else {
              setShowSinglePaymentErrorAlert(true);
              setSinglePaymentError(ErrorMessage);
            }
          } catch (e) {
            setShowSinglePaymentErrorAlert(true);
            setSinglePaymentError(e instanceof Error ? e.message : 'An error occurred during PayPal withdrawal.');
          } finally {
            setLoading(false);
          }
        })();
      } else if (withdrawRequest?.PaymentMethod === 'Venmo') {
        (async () => {
          try {
            setLoading(true);
            const venmoResponse = await venmoWithdraw({
              VenmoToken: withdrawRequest.Contact,
              Amount: withdrawRequest?.Amount || 0,
              ClientAccountID: vp.toBool(ClientAccountsEnabled) ? withdrawRequest?.ClientAccount : '',
              Notes: withdrawRequest?.Notes,
            });
            const { Success, ErrorMessage } = venmoResponse;

            if (Success === true) {
              history.push({
                pathname: `/transactions-history/details/VenmoWithdrawal/${venmoResponse.TransactionID}/${
                  withdrawRequest.Currency || ''
                }/`,
                state: {
                  transactionId: venmoResponse.TransactionID,
                  activeModule: 'singlePayment',
                  hasNewTransactionButton: true,
                },
              });

              setCanSubmit(false);
            } else if (Success === false) {
              setShowSinglePaymentErrorAlert(true);
              setSinglePaymentError(ErrorMessage);
            }
          } catch (e) {
            setShowSinglePaymentErrorAlert(true);
            setSinglePaymentError(e instanceof Error ? e.message : 'An error occurred during PayPal withdrawal.');
          } finally {
            setLoading(false);
          }
        })();
      }
    }
    setCanSubmitTransaction(false);
  }, [canSubmitTransaction, canSubmit]);

  useEffect(() => {
    if (shouldSendPaylink === true && paylinkResponse) {
      const { Success, ErrorMessage } = paylinkResponse;

      if (Success === true) {
        setPayLink(paylinkResponse.Link);
        // setShowSendPaylinkSuccessAlert(true);
        // show review page
        viewState('paylink');
      } else if (Success === false) {
        setShowSendPaylinkErrorAlert(true);
        setSendPaylinkError(ErrorMessage);
      }
      setShouldSendPaylink(false);
    }
  }, [paylinkResponse]);

  const dialogueHandlers = {
    handlePaylinkDialogue: (handlerProps: DialogueHandlerProps2<SendPaylinkDialogueHandler>) => {
      const { type, state: payLinkState, action = 'default' } = handlerProps;

      const dialogueHandlers2: SendPaylinkDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowSendPaylinkErrorAlert(false);
            },
          },
          view: {
            default: () => {
              setShowSendPaylinkErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setShowSendPaylinkSuccessAlert(false);
            },
          },
          view: {
            default: () => {
              setShowSendPaylinkSuccessAlert(false);
              onSubNavClick('paylinkHistory', '/elinx-history', 'eLinx History');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers2[type][payLinkState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };
  const handlers = {
    handleBalanceForCurrency: (currency: string) => {
      getAccountBalance({
        PortalUser: LoginUserName,
        Currency: currency as vp.SupportedCurrencies,
      });
    },

    handleSinglePaymentDialogue: (handlerProps: DialogueHandlerProps<SinglePaymentDialogueHandler>) => {
      const { type, state: handlerState, action = 'default' } = handlerProps;
      const dialogueHandler: SinglePaymentDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowSinglePaymentErrorAlert(false);
            },
          },
          cancel: {
            default: () => {
              setShowSinglePaymentErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setSubmissionConfirmed(true);
              setShowSinglePaymentConfirmAlert(false);
            },
          },
          cancel: {
            default: () => {
              setShowSinglePaymentConfirmAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandler[type][handlerState];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    handleSubmit: (values: SinglePayment) => {
      if (isUnderReview) {
        setSubmissionConfirmed(true);
        setSinglePaymentValue(values);
        setSinglePaymentValuesPresent(true);
      } else {
        setWithdrawRequest({
          ...values,
          Amount: Number(String(values.Amount)?.replaceAll(',', '')),
        });
        setIsUnderReview(true);
      }
    },
    onSinglePaymentChange: (
      _values: SinglePayment,
      errors: FormikErrors<SinglePayment>,
      touched: FormikTouched<SinglePayment>,
    ) => {
      resetBankAccount();
      setFormErrors(errors);
      setFormTouched(touched);
      setWithdrawRequest(_values);
      setContactClientAccountID(_values.ClientAccount);
      setPaymentMethod(_values.PaymentMethod);
      setSelectedCurrency(_values?.Currency || 'CAD');

      if (
        _values.FinancialInstitutionNumber !== '' &&
        withdrawRequest.FinancialInstitutionNumber !== _values.FinancialInstitutionNumber
      ) {
        const FinancialInstitutionID = getFinancialInstitutionIDByNumber(_values.FinancialInstitutionNumber);
        setFormattedFinancialInstitutionBranches({});
        if (FinancialInstitutionID) {
          fetchFinancialInstitutionBranches({ FinancialInstitutionID: Number(FinancialInstitutionID) });
        }
      }

      if (withdrawRequest.Contact !== _values.Contact) {
        if (getAccountCreationValue(withdrawRequest.PaymentMethod) === 'bank' && bankAccountInfo !== undefined) {
          setSelectedBank(bankAccountInfo);
        }
      }

      if (_values.FinancialInstitutionNumber === '') {
        setFormattedFinancialInstitutionBranches({});
      }

      setCreateAccount(_values.Contact === 'provide');
      setCanSubmit(true);
    },
  };

  useEffect(() => {
    if (submitTransaction) {
      setTimeout(() => {
        setSubmitTransaction(false);
      }, 500);
    }
  }, [submitTransaction]);

  useEffect(() => {
    if (isUnderReview) {
      if (reviewRef !== null && reviewRef.current !== null) {
        reviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [isUnderReview]);

  useEffect(() => {
    if (tokenProp !== '') {
      fetchClientBankAccounts({
        PortalUser: LoginUserName,
        ClientControlled: false,
        Token: tokenProp,
        Timezone: userSettings.timezone,
      });
    }
  }, [tokenProp]);

  return (
    <div ref={reviewRef}>
      <Box mb="md" jc="sb" flx="col" gap="lg">
        <Box mb="md" jc="sb">
          {isUnderReview ? <Title title="Review and Submit" /> : <Title title={getModuleTitle()} />}
          {view !== 'paylink' && availableBalance ? (
            <AvailableBalance availableBalance={availableBalance || '0.00'} />
          ) : (
            ''
          )}
        </Box>
        <SendPaylinkContext.Provider value={SendPaylink}>
          <PendingDataProvider>
            <InteracInfoContext.Provider value={interacInfoWithUpdate}>
              <SinglePaymentBankInfoContext.Provider
                value={{
                  bankAccount: bankAccountInfo,
                  updateBankAccount,
                }}
              >
                {view === 'form' && (
                  <SinglePaymentForm
                    hasNoBankAccount={hasNoBankAccount}
                    hasPayPalAccount={hasPayPalAccount}
                    hasVenmoAccount={hasVenmoAccount}
                    resetPendingData={resetPendingData}
                    isUnderReview={isUnderReview}
                    setIsUnderReview={setIsUnderReview}
                    financialInstitutions={FinancialInstitutions}
                    financialInstitutionBranches={FormattedFinancialInstitutionBranches}
                    handlers={handlers}
                    defaultValues={withdrawRequest}
                    defaultTouches={formTouched}
                    SinglePaymentContext={SinglePaymentContext}
                    setIsVisible={setIsVisible}
                    selectedCurrency={selectedCurrency}
                    selectedclientAccountDetail={getSelectedClientAccountDetail}
                    accountHolderName={accountHolderName}
                  />
                )}
              </SinglePaymentBankInfoContext.Provider>
              {view === 'paylink' && paylinkResponse && <PaylinkCreationPage eLinx={paylinkResponse} />}
              <PaylinkDialogue handleDialogue={dialogueHandlers.handlePaylinkDialogue} />
            </InteracInfoContext.Provider>
          </PendingDataProvider>
        </SendPaylinkContext.Provider>
        <ElinxSettingsDialogue
          isVisible={isVisible}
          onCloseModal={() => setIsVisible(false)}
          onFetchElinxSettings={getElinxSettings}
        />
      </Box>
    </div>
  );

  function getFinancialInstitutionIDByNumber(number: string) {
    for (const key in financialInstitutionsResp.FinancialInstitutions) {
      if (financialInstitutionsResp.FinancialInstitutions[key].FinancialInstitutionNumber.toString() === number) {
        return financialInstitutionsResp.FinancialInstitutions[key].FinancialInstitutionID;
      }
    }
    return null; // Return null if the number is not found
  }

  function getAccountCreationValue(initialValue: string): string {
    if (initialValue === 'Bank Account' || initialValue === 'RTP' || initialValue === 'Fedwire') {
      return 'bank';
    }
    if (initialValue === 'VoPay Instant') {
      return 'bank';
    }
    if (initialValue === 'Interac e-Transfer') {
      return 'e-transfer';
    }
    if (initialValue === 'Credit Card') {
      return 'creditCard';
    }
    return initialValue;
  }

  function setSubmitData(values: SinglePayment) {
    const clientAccountsEnabled = vp.toBool(ClientAccountsEnabled);
    const isCompanyName = values?.CompanyName !== '';

    setCommon({
      PortalUser: AccountID || '',
      Amount: values.Amount || 0,
      FinancialInstitutionNumber: clientAccountsEnabled ? '' : values?.FinancialInstitutionNumber || '',
      BranchTransitNumber: clientAccountsEnabled ? '' : values?.BranchTransitNumber || '',
      ABARoutingNumber: clientAccountsEnabled ? '' : values?.ABARoutingNumber || '',
      AccountNumber: clientAccountsEnabled ? '' : values?.AccountNumber || '',
      FirstName: !isCompanyName ? values?.FirstName || '' : '',
      LastName: !isCompanyName ? values?.LastName || '' : ' ',
      CompanyName: isCompanyName ? values?.CompanyName || '' : '',
      PhoneNumber: vp.NumbersOnly(values?.RecipientPhoneNumber || ''),
      Address1: values?.Address1 || '',
      City: values?.City || '',
      Province: values?.Province || '',
      Country: Country || '',
      PostalCode: values?.PostalCode || '',
      ClientReferenceNumber: values?.ClientReferenceNumber || '',
      FlinksAccountID: '',
      FlinksLoginID: '',
      TransactionLabel: values.TransactionLabel || '',
      Token: '',
    });
  }

  function buildEmbedUrlParams(values: NewAccountData): vp.EmbedUrlRequest {
    const embedUrlData: vp.EmbedUrlRequest = {
      RedirectURL: 'https://request-dev.vopay.com/',
      Language: values.language || 'en',
      RedirectMethod: 'JavascriptMessage',
      PaymentSelectionMethod:
        values.PaymentMethod === 'Credit Card'
          ? 'credit'
          : values.PaymentMethod == 'Interac e-Transfer'
            ? 'email'
            : values.PaymentMethod === 'PayPal'
              ? 'paypal'
              : values.PaymentMethod === 'Venmo'
                ? 'venmo'
                : 'bank',
      AccountSelectionMethod:
        values.AccountConnectionMethod.includes('|') || values.AccountConnectionMethod === ''
          ? 'any'
          : values.AccountConnectionMethod.toLowerCase(),
      ClientControlled: vp.toBool(ClientAccountsEnabled),
      ClientReferenceNumber: values.ClientReferenceNumber,
      TermsAndConditions: values.termsAndConditions,
      Country: values.Country,
      ClientAccountID: contactClientAccountID,
      Version: 'v2',
    };
    return embedUrlData;
  }
};

export default SinglePaymentContainer;
